import React from 'react';
import {
  Pressable, StyleSheet, Text, View,
} from 'react-native';
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import { useDispatch, useSelector } from 'react-redux';
import PressableStyled, { PressableState } from '../PressableStyled';
import Colors from '../../constants/Colors';
import TriangleDown from '../TriangleDown';
import CheckBox from '../16pxIcons/CheckBox';
import {
  SensorsFilterState, updateSensorFilter, resetSensorFilter,
} from '../../redux/sensorsFilterSlice';
import ScrollViewStyled from '../ScrollViewStyled';
import { AssetTypeTitles, MeterLabel, SensorLabel } from '../../constants/Types';
import Style from '../../constants/Style';

interface SensorRowProps {
  label: string,
  enabled: boolean,
  onPress: () => void,
  bold?: boolean,
  testID?: string,
}

export const SensorRow = ({
  label, enabled, onPress, bold, testID,
}: SensorRowProps) => <Pressable
  style={({ hovered, pressed }: PressableState) => [
    styles.sensorRow, hovered && Style.listItemHovered, pressed && Style.listItemPressed,
  ]}
  {...{ onPress, testID }}
>
    <CheckBox
      color={enabled ? Colors.light.hereworksBlue500 : Colors.light.hereworksBlue200}
      checkcolor={enabled ? Colors.light.hereworksBlue500 : '#fff'}
    />
    <Text
      style={[styles.typeText, bold && styles.boldText]}
      selectable
    >
      {label}
    </Text>
  </Pressable>;

interface TabButtonProps {
  onPress: () => void,
  title: string,
  selected: boolean,
  testID?: string,
}

const SensorTabButton = ({
  onPress, title, selected, testID,
}: TabButtonProps) => {
  const filterHeight = useSharedValue<number>(0);
  const sensorsFilter: SensorsFilterState = useSelector((state:
    { sensorsFilter: SensorsFilterState }) => state.sensorsFilter);
  const selectedFilterCount: number = Object.values(sensorsFilter)
    .filter((value: boolean) => value).length;

  const heightStyle = useAnimatedStyle(() => ({
    height: filterHeight.value,
    borderWidth: filterHeight.value === 0 ? 0 : 1.5,
  }));

  const dispatch = useDispatch();

  return (<>
    <Pressable
      {...{ onPress, testID }}
      style={({ hovered }: PressableState) => [
        styles.container, hovered && styles.containerHovered, selected && styles.containerSelected,
      ]}
      disabled={selected}
    >
      <Text style={[styles.text, selected && styles.textSelected]}>{title}</Text>
      {(selected && selectedFilterCount > 0) ? <View
        style={styles.selectedCircle}>
        <Text style={styles.selectedText}>{selectedFilterCount}</Text>
      </View> : null}
      {(selected) ? <PressableStyled
        onPress={() => {
          if (filterHeight.value === 0) filterHeight.value = withTiming(273);
          else filterHeight.value = withTiming(0);
        }}
        testID='map-sensors-drop-down-btn'
      >
        <TriangleDown />
      </PressableStyled> : null}
    </Pressable>
    {selected && <Animated.View style={[styles.filterContainer, heightStyle]}>
      <View style={styles.listHeader}>
        <Text style={styles.filterTitle}>{'Sensors'}</Text>
        {selectedFilterCount > 0 ? <PressableStyled onPress={() => dispatch(resetSensorFilter())}>
          <Text style={styles.resetText}>{'RESET'}</Text>
        </PressableStyled> : <Text style={styles.showingAllText}>{'Showing all sensors'}</Text>}
      </View>
      <ScrollViewStyled style={styles.scrollView}>
        {[...Object.values(SensorLabel), ...Object.values(MeterLabel)]
          .map((key: string) => <SensorRow
            key={`sensor_filter_${key}`}
            label={`${AssetTypeTitles[key]} (${key})`}
            enabled={sensorsFilter[key]}
            onPress={() => dispatch(updateSensorFilter({ key, value: !sensorsFilter[key] }))}
          />)}
      </ScrollViewStyled>
    </Animated.View>}
  </>);
};

const styles = StyleSheet.create({
  container: {
    ...Style.row,
    paddingVertical: 4,
    paddingHorizontal: 12,
    borderRadius: 4,
    borderWidth: 1,
    marginHorizontal: 4,
    borderColor: Colors.light.hereworksBlue300,
  },
  containerSelected: {
    backgroundColor: Colors.light.hereworksBlue500,
    borderColor: Colors.light.hereworksBlue500,
    paddingRight: 6,
  },
  containerHovered: {
    backgroundColor: Colors.light.hereworksBlue50,
    borderColor: 'transparent',
  },
  text: {
    fontSize: 12,
    lineHeight: 20,
    fontFamily: 'Roboto_500Medium',
    color: Colors.light.hereworksBlue400,
  },
  textSelected: {
    color: '#FFF',
    paddingEnd: 6,
  },
  filterContainer: {
    position: 'absolute',
    width: 274,
    backgroundColor: '#fff',
    top: 44,
    right: 0,
    overflow: 'hidden',
    borderRadius: 10,
    borderColor: Colors.light.hereworksBlue100,
  },
  filterTitle: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
  },
  typeText: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
    flex: 1,
    marginStart: 8,
  },
  selectedCircle: {
    backgroundColor: Colors.light.hereworksDarkBlue50,
    width: 18,
    height: 18,
    borderRadius: 9,
    justifyContent: 'center',
    alignItems: 'center',
  },
  selectedText: {
    fontFamily: 'Roboto_700Bold',
    fontSize: 13,
    lineHeight: 18,
    color: Colors.light.hereworksBlue500,
  },
  boldText: {
    fontFamily: 'Poppins_600SemiBold',
  },
  sensorRow: {
    ...Style.row,
    paddingHorizontal: 12,
    paddingVertical: 9,
  },
  divider: {
    marginStart: 16,
    paddingStart: 4,
    borderStartWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
  },
  scrollView: {
    marginEnd: 4,
  },
  listHeader: {
    ...Style.row,
    borderBottomWidth: 1.5,
    borderColor: Colors.light.hereworksBlue100,
    paddingHorizontal: 12,
    paddingVertical: 9,
  },
  resetText: {
    fontSize: 12,
    lineHeight: 14,
    fontFamily: 'Roboto_500Medium',
    color: Colors.light.hereworksBlack500,
    textDecorationLine: 'underline',
  },
  showingAllText: {
    fontSize: 12,
    lineHeight: 18,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
  },
});

export default SensorTabButton;
